import {
  useCallback,
  useEffect,
  useState,
  memo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import dayjsPluginUTC from 'dayjs-plugin-utc';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useAnalytics } from 'use-analytics';
import DropdownSelectFilter from '../../../Filters/DropdownSelectFilter';
import DateSelectorFilter from '../../../Filters/DateSelectorFilter';
import ErrorTooltip from '../../../ErrorTooltip';
import SearchAutocomplete from '../../../SearchAutocomplete';
import {
  SearchStatus,
  CarUISearchTypeOptions,
  CarUISearchType,
  AutocompleteType,
  FiltersInitialValue,
  SearchModes,
  PageLinks,
  HomeSearchInputFormVariants,
  TravelTypes,
  TwAttributionVariants,
} from '../../../../constants';
import { autocompleteRequest } from '../../../../store/slices/autocomplete';
import { updateCarSearchObj, carSearch } from '../../../../store/slices/car';
import carSearchInput from '../../../../helpers/selectors/carSearchInput';
import {
  resetSearchResults, setCarTripSearchInputs, setCarTripSearchParam,
  setCarSearchObjFromQueryString, setTripSearchInputs,
} from '../../../../store/slices/tripSearch';
import CarsFilterBar from '../CarsFilterBar';
import useSearchObjFromQueryString from '../../../../hooks/useSearchObjFromQueryString';
import { resetTrips } from '../../../../store/slices/trip';
import setURLParams from '../../../../utils/setURLParams';
import RateCodePreferencesFilter from '../CarsFilterBar/RateCodePreferencesFilter';
import { getTravelPolicyLeadDays } from '../../../../store/slices/user';
import TransmissionTypesFilter from '../CarsFilterBar/TransmissionTypesFilter';
import CustomerLookup from '../../../CustomerLookup';
import TwPoweredByLogo from '../../../TwPoweredByLogo';

dayjs.extend(dayjsPluginUTC);

const initialFormValidations = {
  pickup: null,
  dropoff: null,
  pickupDateTime: null,
  returnDateTime: null,
};
const CarSearchBar = ({
  tripId, selectedTab, filterBgColor, searchTypeTextColor,
}) => {
  const { track } = useAnalytics();
  const isTripSearch = !tripId;
  const ENABLE_CUSTOMER_LOOKUP = false; // Test well when enabling this flag
  const leadDays = useSelector(getTravelPolicyLeadDays(TravelTypes.carrental));
  const { isLoggedIn, travelAgency } = useSelector((store) => store.user);
  const [formValidations, setFormValidations] = useState(initialFormValidations);
  const dispatch = useDispatch();
  const router = useRouter();
  const {
    searchInputs: tripSearchInputs, selectedSearchMode, currencyCode,
    searchStatus: tripSearchStatus,
  } = useSelector((store) => store.tripSearch);
  const { searchMode: searchModeQuery, pageName } = router.query;
  const doShowRateCodeFilter = pageName === PageLinks.ChangeCar.pageName
    || pageName === PageLinks.AddCar.pageName || !isTripSearch;
  const doShowTransmissionFilter = pageName === PageLinks.ChangeCar.pageName
    || pageName === PageLinks.AddCar.pageName || !isTripSearch;
  const {
    searchInputs,
    searchStatus,
  } = useSelector(carSearchInput(tripId, selectedTab, isTripSearch));
  const staterooms = useSelector((store) => store.tripSearch.searchInputs.staterooms);
  const { carTabs } = useSelector((store) => store.car.carSearches[tripId] || {});
  const {
    carsAutocompleteResults,
  } = useSelector((store) => store.autocomplete);
  const homeSearchInputFormVariant = travelAgency
    && travelAgency.siteTheme
    && travelAgency.siteTheme.homeSearchInputFormVariant;
  const doCenterAlignFilters = !isLoggedIn
    && homeSearchInputFormVariant === HomeSearchInputFormVariants.CENTER_ALIGNED;
  const gapBetweenSearchbarFields = travelAgency && travelAgency.siteTheme
    && travelAgency.siteTheme.gapBetweenSearchbarFields;
  const doShowGradientOnSearchBtn = travelAgency && travelAgency.siteTheme
    && travelAgency.siteTheme.showGradientOnSearchBtn;
  let textToAppendPlaceholder = 'port or airport';
  let autocompleteTypes = [...AutocompleteType.AIRPORTS, ...AutocompleteType.PORT_OF_DEPARTURE];
  if (isTripSearch || (selectedSearchMode || SearchModes.CRUISE_TRIP) !== SearchModes.CRUISE_TRIP) {
    autocompleteTypes = [...AutocompleteType.AIRPORTS, ...AutocompleteType.ADDRESSES];
    textToAppendPlaceholder = 'Address or Airport';
  }
  const rentalCarAddressInputPlaceholder = travelAgency && travelAgency.filterSettings
    && travelAgency.filterSettings.rentalCarAddressInputPlaceholder;
  const doHideGuestsFilterForCarRental = !!(travelAgency && travelAgency.filterSettings
    && travelAgency.filterSettings.doHideGuestsFilterForCarRental);
  const twAttributionVariant = travelAgency?.siteTheme?.TwAttributionVariant;
  const doShowAttributionUnderSearchBar = twAttributionVariant
    === TwAttributionVariants.UNDER_SEARCH_BAR;

  const resetValidation = useCallback(() => {
    setFormValidations(initialFormValidations);
  }, []);

  const validateSearch = useCallback((obj, setValidations, doShowValidationErrors = true) => {
    let isValid = false;
    const validationObj = { ...initialFormValidations };
    isValid = !!(obj.pickupCode && obj.dropoffCode && obj.pickupDateTime && obj.returnDateTime);
    validationObj.pickup = obj.pickupCode ? null : 'Pick up port is required';
    validationObj.dropoff = obj.dropoffCode ? null : 'Drop-off port is required';
    validationObj.pickupDateTime = obj.pickupDateTime ? null : 'Pickup date is required';
    validationObj.returnDateTime = obj.returnDateTime ? null : 'Drop-off date is required';
    // Lead day validations based on user status
    const minCheckInDate = dayjs().startOf('day').add(leadDays, 'day');
    if (dayjs(obj.pickupDateTime).isBefore(minCheckInDate)) {
      validationObj.pickupDateTime = `Pickup date cannot be earlier than ${leadDays} ${leadDays === 1 ? 'day' : 'days'} from today.`;
      isValid = false;
    }
    if (doShowValidationErrors) setValidations(validationObj);
    return isValid;
  }, [leadDays]);

  const handleChange = useCallback((value) => {
    if (isTripSearch) {
      dispatch(setCarTripSearchInputs({
        tripId,
        ...value,
      }));
    } else {
      dispatch(updateCarSearchObj({
        tripId,
        carTabs: {
          ...carTabs,
          [selectedTab]: {
            ...carTabs[selectedTab],
            searchObj: {
              ...carTabs[selectedTab].searchObj,
              ...value,
            },
          },
        },
      }));
    }
  }, [isTripSearch, dispatch, tripId, carTabs, selectedTab]);

  const handleInputChange = useCallback((query) => {
    dispatch(autocompleteRequest({
      autocompleteTypes,
      responseType: 'carsAutocompleteResults',
      query,
    }));
  }, [autocompleteTypes, dispatch]);

  const handleChangeCustomer = useCallback((customerInfo) => {
    const updatedStaterooms = Array.isArray(staterooms) ? [...staterooms] : [];
    if (!updatedStaterooms.length) updatedStaterooms.push({});
    updatedStaterooms[0] = {
      ...updatedStaterooms[0],
      customerId: customerInfo && customerInfo.customerId
        ? customerInfo.customerId : undefined,
      customerEmail: customerInfo && customerInfo.email
        ? customerInfo.email : undefined,
    };
    dispatch(setTripSearchInputs({ staterooms: updatedStaterooms }));
    dispatch(setCarTripSearchParam({ staterooms: updatedStaterooms }));
  }, [dispatch, staterooms]);

  const handleSearch = useCallback((event, doShowValidationErrors = true) => {
    if (event) event.preventDefault();
    if (isTripSearch) {
      const { car } = tripSearchInputs;
      const updatedTripSearchInputs = {
        ...tripSearchInputs,
        car: { ...car, selectedCurrency: currencyCode },
      };
      if (validateSearch(searchInputs, setFormValidations, doShowValidationErrors)) {
        dispatch(resetTrips());
        dispatch(setCarTripSearchParam({
          ...searchInputs,
          page: 1,
          staterooms,
        }));
        dispatch(carSearch({ isTripSearch: true }));
        setURLParams({
          searchObj: updatedTripSearchInputs,
          searchedMode: SearchModes.CAR,
          router,
        });
        track('carSearch', { isTripSearch });
      }
    } else {
      const payload = {
        tripId,
        selectedTab,
      };
      dispatch(updateCarSearchObj({
        tripId,
        carTabs: {
          ...carTabs,
          [selectedTab]: {
            ...carTabs[selectedTab],
            searchObj: {
              ...carTabs[selectedTab].searchObj,
              fromBudget: FiltersInitialValue.car.fromBudget,
              toBudget: FiltersInitialValue.car.toBudget,
              page: 1,
            },
          },
        },
        defaultSearchObj: {
          fromBudget: FiltersInitialValue.car.fromBudget,
          toBudget: FiltersInitialValue.car.toBudget,
        },
      }));
      if (validateSearch(searchInputs, setFormValidations)) {
        track('carSearch', { isTripSearch });
        dispatch(carSearch(payload));
      }
    }
  }, [isTripSearch, searchInputs, dispatch, staterooms, track,
    currencyCode, tripSearchInputs, router, tripId, selectedTab, carTabs, validateSearch]);

  useEffect(() => {
    if (isTripSearch) return;
    const {
      pickupCode,
      dropoffCode,
      pickupDateTime,
      returnDateTime,
    } = searchInputs;
    if (pickupCode
      && dropoffCode
      && pickupDateTime
      && returnDateTime
      && searchStatus === SearchStatus.DONE) {
      dispatch(updateCarSearchObj({
        tripId,
        carTabs: {
          ...carTabs,
          [selectedTab]: {
            ...carTabs[selectedTab],
            label: `${pickupCode.airportCode
              ? pickupCode.airportCode : pickupCode.label} - ${dropoffCode.airportCode
              ? dropoffCode.airportCode : dropoffCode.label} (${dayjs(pickupDateTime)
              .format('M/D/YY')} - ${dayjs(returnDateTime).format('M/D/YY')})`,
          },
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, searchStatus]);

  useEffect(() => {
    resetValidation();
  }, [selectedTab, resetValidation]);

  useSearchObjFromQueryString((searchObjFromQueryString) => {
    if (tripSearchStatus !== SearchStatus.INITIAL) return;
    if ((searchModeQuery || SearchModes.CRUISE_TRIP) === selectedSearchMode) {
      dispatch(setCarSearchObjFromQueryString(searchObjFromQueryString));
      if (validateSearch(searchObjFromQueryString, setFormValidations, true)) {
        dispatch(resetTrips());
        dispatch(carSearch({
          isTripSearch: true,
        }));
      }
    } else {
      dispatch(resetSearchResults({ isLoggedIn }));
      handleSearch(null, false);
    }
  }, undefined, isTripSearch);

  return (
    <>
      <div className="flex-wrap">
        <DropdownSelectFilter
          label={CarUISearchTypeOptions[searchInputs.carSearchType].label}
          onChange={(newValue) => handleChange({ carSearchType: newValue })}
          options={CarUISearchTypeOptions}
          defaultOptionValue={CarUISearchType.ENTIREDURATION}
          selectedOptionValue={searchInputs.carSearchType}
          menuWidth="w-64"
          filterButtonMaxWidth="max-w-full"
          filterSpacingClass={clsx('py-3 pr-3', { 'pt-0': doCenterAlignFilters })}
          variant={isTripSearch ? 'transparentWhiteFilterClasses' : 'text'}
          textColorClassName={isTripSearch ? searchTypeTextColor : ''}
        />
      </div>
      <form
        onSubmit={handleSearch}
        onFocus={resetValidation}
        className={clsx(gapBetweenSearchbarFields, { 'md:grid md:grid-cols-10 space-y-2 md:space-y-0 p-2 md:p-0': isTripSearch, 'flex gap-2 flex-wrap text-theme': !isTripSearch })}
      >
        {searchInputs.carSearchType
          ? (
            <>
              <ErrorTooltip
                title={formValidations.pickup || ''}
                open={formValidations.pickup !== null}
                arrow
              >
                <div className={clsx({ 'w-56': !isTripSearch, 'md:col-span-2': isTripSearch })}>
                  <SearchAutocomplete
                    placeholderText={`Enter pick-up ${rentalCarAddressInputPlaceholder || textToAppendPlaceholder}`}
                    variant={isTripSearch ? [SearchAutocomplete.Variants.outlineNoRightRadius,
                      SearchAutocomplete.Variants.outlineNoBorderWidth] : undefined}
                    onChange={(newValue) => handleChange({ pickupCode: newValue })}
                    options={carsAutocompleteResults}
                    onInputChange={handleInputChange}
                    values={searchInputs.pickupCode}
                    popperMinWidth={350}
                    doUseThemeBackground={isTripSearch}
                  />
                </div>
              </ErrorTooltip>
              <ErrorTooltip
                title={formValidations.dropoff || ''}
                open={formValidations.dropoff !== null}
                arrow
              >
                <div className={clsx({ 'w-56': !isTripSearch, 'col-span-2': isTripSearch })}>
                  <SearchAutocomplete
                    popperMinWidth={350}
                    placeholderText={`Enter drop-off ${rentalCarAddressInputPlaceholder || textToAppendPlaceholder}`}
                    onChange={(newValue) => handleChange({ dropoffCode: newValue })}
                    options={carsAutocompleteResults}
                    onInputChange={handleInputChange}
                    values={searchInputs.dropoffCode}
                    variant={isTripSearch ? [SearchAutocomplete.Variants.outlineNoRadius,
                      SearchAutocomplete.Variants.outlineNoBorderWidth] : undefined}
                    doUseThemeBackground={isTripSearch}
                  />
                </div>
              </ErrorTooltip>
            </>
          )
          : (
            <ErrorTooltip
              title={formValidations.pickup || ''}
              open={formValidations.pickup !== null}
              arrow
            >
              <div className={clsx({ 'w-56': !isTripSearch, 'md:col-span-4': isTripSearch })}>
                <SearchAutocomplete
                  placeholderText={`Enter ${rentalCarAddressInputPlaceholder || textToAppendPlaceholder}`}
                  onChange={(newValue) => handleChange({
                    pickupCode: newValue,
                    dropoffCode: newValue,
                  })}
                  options={carsAutocompleteResults}
                  onInputChange={handleInputChange}
                  values={searchInputs.pickupCode}
                  variant={isTripSearch ? [SearchAutocomplete.Variants.outlineNoRightRadius,
                    SearchAutocomplete.Variants.outlineNoBorderWidth] : undefined}
                  popperMinWidth={350}
                  doUseThemeBackground={isTripSearch}
                />
              </div>
            </ErrorTooltip>
          )}
        <ErrorTooltip
          title={formValidations.pickupDateTime || ''}
          open={formValidations.pickupDateTime !== null}
          arrow
        >
          <div className={clsx({ 'md:col-span-2': isTripSearch })}>
            <DateSelectorFilter
              onChange={(newValue) => handleChange({ pickupDateTime: newValue })}
              label="Pick-up Date"
              filterButtonMaxWidth={clsx('max-w-full', { 'w-full': isTripSearch })}
              value={searchInputs.pickupDateTime}
              defaultValue={searchInputs.pickupDateTime}
              variant={isTripSearch ? 'outline-no-radius' : 'outline'}
              format="datetime"
              bgClassName={isTripSearch ? 'bg-searchInput-0' : undefined}
              textColorClassName="text-datepicker-0"
              leadDays={leadDays}
            />
          </div>
        </ErrorTooltip>
        <ErrorTooltip
          title={formValidations.returnDateTime || ''}
          open={formValidations.returnDateTime !== null}
          arrow
        >
          <div className={clsx({ 'col-span-2': isTripSearch })}>
            <DateSelectorFilter
              onChange={(newValue) => handleChange({ returnDateTime: newValue })}
              label="Drop-off Date"
              filterButtonMaxWidth={isTripSearch ? 'w-full' : 'max-w-full'}
              value={searchInputs.returnDateTime}
              defaultValue={searchInputs.returnDateTime}
              variant={isTripSearch ? 'outline-no-radius' : 'outline'}
              format="datetime"
              minDate={searchInputs.pickupDateTime ? dayjs(searchInputs.pickupDateTime) : undefined}
              bgClassName={isTripSearch ? 'bg-searchInput-0' : undefined}
              textColorClassName="text-datepicker-0"
              leadDays={leadDays}
            />
          </div>
        </ErrorTooltip>
        {doShowRateCodeFilter && (
          <RateCodePreferencesFilter
            tripId={tripId}
            selectedTab={selectedTab}
            onChange={handleChange}
          />
        )}
        {doShowTransmissionFilter && (
          <TransmissionTypesFilter
            tripId={tripId}
            selectedTab={selectedTab}
            bgClassName={filterBgColor}
          />
        )}
        <div className="md:col-span-2">
          <button
            type="submit"
            className={clsx('px-8 min-h-12 h-auto bg-searchButton-0 text-searchbtn-0 text-base font-bold h-full', { 'md:rounded-r-2xl w-full': isTripSearch, 'w-max rounded-2xl': !isTripSearch, 'hover:gradient-animation': doShowGradientOnSearchBtn })}
          >
            <span className="font-body">Search</span>
          </button>
        </div>
        {isTripSearch && (
          <div className="md:col-span-12 flex items-center justify-between text-white">
            <div className="flex-1">
              {ENABLE_CUSTOMER_LOOKUP && isTripSearch && (
              <CustomerLookup
                doShowInHeaderFilter
                onChange={handleChangeCustomer}
              />
              )}
              <CarsFilterBar
                tripId={tripId}
                selectedTab={selectedTab}
                alignFiltersCentered={doCenterAlignFilters}
                filterBgColor={filterBgColor}
                doHideGuestsFilterForCarRental={doHideGuestsFilterForCarRental}
              />
            </div>
            {doShowAttributionUnderSearchBar && (
              <div className="flex-shrink-0 ml-4">
                <TwPoweredByLogo doMakeTravelwitsLogoSmaller />
              </div>
            )}
          </div>
        )}
      </form>
    </>
  );
};

CarSearchBar.propTypes = {
  tripId: PropTypes.string,
  selectedTab: PropTypes.number,
  filterBgColor: PropTypes.string,
  searchTypeTextColor: PropTypes.string,
};

CarSearchBar.defaultProps = {
  tripId: undefined,
  selectedTab: 0,
  filterBgColor: undefined,
  searchTypeTextColor: 'text-tabsText-0',
};
export default memo(CarSearchBar);
